// Tab "Permissões" — overrides individuais do usuário (UserPermissionSetting).
//
// Modelo (espelha ABP do FLOW):
//   - Padrão        — sem override; permissão segue do(s) role(s) do user.
//   - Conceder      — grant explícito; user tem mesmo se nenhum role concede.
//   - Bloquear      — revoke explícito; user NÃO tem mesmo se algum role concede.
//
// UX: dois ícones-checkbox pequenos por permissão (✓ Conceder | ✗ Bloquear).
// Marcar um desmarca automaticamente o outro. Os dois desmarcados = padrão.

import React, { useEffect, useState, useCallback } from "react";
import { Button, Input } from "reactstrap";

import {
  getUserPermissionOverrides,
  setUserPermissionOverrides,
} from "services/usersApi";
import { getPermissionsTree } from "services/rolesApi";
import { useToast } from "hooks/toast";

function flattenTree(node, acc = []) {
  if (!node) return acc;
  if (node.name) acc.push(node);
  if (node.children) {
    for (const c of Object.values(node.children)) flattenTree(c, acc);
  }
  return acc;
}

// Ícone-checkbox pequeno (botão com ícone interno + estado visual).
function MarkerButton({ active, color, label, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      style={{
        background: active ? color : "transparent",
        color: active ? "#fff" : "#8fa3c8",
        border: `1px solid ${active ? color : "#2b3553"}`,
        borderRadius: 4,
        padding: "2px 8px",
        fontSize: 11,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        minWidth: 28,
      }}
    >
      {label}
    </button>
  );
}

function PermissionRow({ node, depth, granted, revoked, onSet }) {
  const isGranted = granted.has(node.name);
  const isRevoked = revoked.has(node.name);

  return (
    <div
      style={{
        paddingLeft: depth * 18,
        paddingTop: 4,
        paddingBottom: 4,
        display: "flex",
        alignItems: "center",
        gap: 8,
        borderBottom: depth === 0 ? "1px solid #2b3553" : "none",
        marginTop: depth === 0 ? 8 : 0,
        color: "#f5f7fb",
        fontSize: 13,
      }}
    >
      <span style={{ flex: 1, fontWeight: depth === 0 ? 600 : 400 }}>
        {node.displayName || node.name}
        <span style={{ color: "#8fa3c8", fontSize: 11, marginLeft: 6 }}>
          ({node.name})
        </span>
      </span>
      <div style={{ display: "flex", gap: 4 }}>
        <MarkerButton
          active={isGranted}
          color="#27ae60"
          label="✓"
          onClick={() => onSet(node.name, isGranted ? "default" : "grant")}
        />
        <MarkerButton
          active={isRevoked}
          color="#ec250d"
          label="✗"
          onClick={() => onSet(node.name, isRevoked ? "default" : "revoke")}
        />
      </div>
    </div>
  );
}

export default function UserPermissionsTab({ user }) {
  const [tree, setTree] = useState(null);
  const [granted, setGranted] = useState(new Set());
  const [revoked, setRevoked] = useState(new Set());
  const [originalGranted, setOriginalGranted] = useState(new Set());
  const [originalRevoked, setOriginalRevoked] = useState(new Set());
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([getPermissionsTree(), getUserPermissionOverrides(user.id)])
      .then(([treeData, overrides]) => {
        if (cancelled) return;
        setTree(treeData);
        const g = new Set(overrides.granted || []);
        const r = new Set(overrides.revoked || []);
        setGranted(g);
        setRevoked(r);
        setOriginalGranted(g);
        setOriginalRevoked(r);
      })
      .catch((err) => {
        console.error("[UserPermissionsTab] load:", err);
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro",
          description: "Não foi possível carregar permissões",
          icon: "tim-icons icon-alert-circle-exc",
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, addToast]);

  const handleSet = useCallback((name, state) => {
    // state: 'grant' | 'revoke' | 'default'
    setGranted((prev) => {
      const next = new Set(prev);
      if (state === "grant") next.add(name);
      else next.delete(name);
      return next;
    });
    setRevoked((prev) => {
      const next = new Set(prev);
      if (state === "revoke") next.add(name);
      else next.delete(name);
      return next;
    });
  }, []);

  const handleClearAll = useCallback(() => {
    setGranted(new Set());
    setRevoked(new Set());
  }, []);

  const handleSave = useCallback(async () => {
    if (!user) return;
    setSaving(true);
    try {
      const result = await setUserPermissionOverrides(user.id, {
        granted: Array.from(granted),
        revoked: Array.from(revoked),
      });
      addToast({
        place: "tr",
        type: "success",
        title: "Salvo",
        description: `Conceder: ${result.granted.added.length} +/${result.granted.removed.length} -, Bloquear: ${result.revoked.added.length} +/${result.revoked.removed.length} -`,
        icon: "tim-icons icon-check-2",
      });
      setOriginalGranted(new Set(granted));
      setOriginalRevoked(new Set(revoked));
    } catch (err) {
      console.error("[UserPermissionsTab] save:", err);
      const message =
        (err.response && err.response.data && err.response.data.error) ||
        "Erro ao salvar permissões";
      const invalid = err.response && err.response.data && err.response.data.invalid;
      const conflicting = err.response && err.response.data && err.response.data.conflicting;
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro",
        description: invalid
          ? `${message}: ${invalid.join(", ")}`
          : conflicting
          ? `${message}: ${conflicting.join(", ")}`
          : message,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setSaving(false);
    }
  }, [user, granted, revoked, addToast]);

  const isDirty =
    granted.size !== originalGranted.size ||
    revoked.size !== originalRevoked.size ||
    Array.from(granted).some((n) => !originalGranted.has(n)) ||
    Array.from(revoked).some((n) => !originalRevoked.has(n));

  if (loading) {
    return (
      <div style={{ color: "#8fa3c8", textAlign: "center", padding: 20 }}>
        Carregando permissões…
      </div>
    );
  }
  if (!tree) return null;

  // Filtra árvore se houver busca: incluir nó cujo name OU displayName bate,
  // mantendo ancestrais para preservar hierarquia visual.
  const flat = flattenTree(tree);
  const filterLower = filter.trim().toLowerCase();
  const matchedNames = filterLower
    ? new Set(
        flat
          .filter(
            (n) =>
              n.name.toLowerCase().includes(filterLower) ||
              (n.displayName || "").toLowerCase().includes(filterLower)
          )
          .map((n) => n.name)
      )
    : null;

  // Recursivo render
  const renderNode = (node, depth) => {
    const childArr = node.children ? Object.values(node.children) : [];
    const selfMatches = !matchedNames || matchedNames.has(node.name);
    const childRenders = childArr
      .map((c) => renderNode(c, depth + 1))
      .filter(Boolean);
    if (!selfMatches && childRenders.length === 0) return null;
    return (
      <React.Fragment key={node.name}>
        <PermissionRow
          node={node}
          depth={depth}
          granted={granted}
          revoked={revoked}
          onSet={handleSet}
        />
        {childRenders}
      </React.Fragment>
    );
  };

  return (
    <div>
      <div
        style={{
          background: "rgba(29,140,248,0.08)",
          borderLeft: "3px solid #1d8cf8",
          padding: "10px 14px",
          color: "#8fa3c8",
          fontSize: 12,
          marginBottom: 12,
        }}
      >
        Overrides individuais do usuário. <strong style={{ color: "#27ae60" }}>✓ Conceder</strong>{" "}
        libera a permissão mesmo se o role não concede.{" "}
        <strong style={{ color: "#ec250d" }}>✗ Bloquear</strong> nega mesmo se
        o role concede. Sem nenhum dos dois = herda do role.
      </div>

      <Input
        type="text"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filtrar permissões…"
        style={{ marginBottom: 12 }}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <div style={{ color: "#8fa3c8", fontSize: 12 }}>
          <span style={{ color: "#27ae60", fontWeight: 600 }}>{granted.size}</span>{" "}
          conceder ·{" "}
          <span style={{ color: "#ec250d", fontWeight: 600 }}>{revoked.size}</span>{" "}
          bloquear
        </div>
        <Button color="secondary" size="sm" outline onClick={handleClearAll}>
          Limpar overrides
        </Button>
      </div>

      <div
        style={{
          maxHeight: 400,
          overflowY: "auto",
          border: "1px solid #2b3553",
          borderRadius: 4,
          padding: "4px 8px",
        }}
      >
        {renderNode(tree, 0)}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 16,
        }}
      >
        <Button
          color="success"
          onClick={handleSave}
          disabled={saving || !isDirty}
        >
          {saving ? "Salvando…" : "Salvar overrides"}
        </Button>
      </div>
    </div>
  );
}
